'use client';

import { memo, type ReactNode, useEffect, useState } from 'react';

import { useIsLoggedIn } from '@air/utils-auth';

import { LoadingState } from '~/app/(authorized)/_components/LoadingState';
import { NoProductState } from '~/app/(authorized)/_components/NoProductState';
import { useShopifyProductId } from '~/hooks/shopify/useShopifyProductId';
import { useHandleUnauthenticatedApiError } from '~/hooks/useHandleUnauthenticatedApiError';
import { useUnauthenticatedRedirect } from '~/hooks/useUnauthenticatedRedirect';

export type AuthorizedLayoutProps = {
  children: ReactNode;
};

const AuthorizedGuard = memo(({ children }: { children: ReactNode }) => {
  useUnauthenticatedRedirect();
  useHandleUnauthenticatedApiError();
  const { isLoggedInFetched } = useIsLoggedIn();

  if (!isLoggedInFetched) {
    return <LoadingState />;
  }

  return <>{children}</>;
});

AuthorizedGuard.displayName = 'AuthorizedGuard';

export default function AuthorizedLayout({ children }: AuthorizedLayoutProps) {
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => setIsMounted(true), []);

  const productId = useShopifyProductId();

  if (!isMounted) {
    return <LoadingState />;
  }
  if (!productId) {
    return <NoProductState />;
  }

  return <AuthorizedGuard>{children}</AuthorizedGuard>;
}
